import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { portraits } from '../data/media';
import { Mascot } from './Mascot';
export function Testimonials() {
  const {
    t
  } = useLanguage();
  const [index, setIndex] = useState(0);
  const items = t.testimonials.items;
  const current = items[index];
  const go = (next: number) => setIndex((next + items.length) % items.length);
  return <section className="relative w-full overflow-hidden bg-ink py-16 lg:py-20" data-id="element-342" data-mp="31yl4z0tjvmmr">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-brand-500" aria-hidden="true" data-id="element-343" data-mp="2x1l0uvg9gtya" />
      <Mascot size={190} tone="light" className="pointer-events-none absolute -bottom-12 -right-10 opacity-[0.07] lg:opacity-10" data-id="element-344" data-mp="2s2rxvwylolcd" />

      <div className="relative mx-auto max-w-shell px-4 sm:px-6 lg:px-8" data-id="element-345" data-mp="qac1axrhk6zc">
        <div className="max-w-2xl" data-id="element-346" data-mp="q892ppc9p5j0">
          <h2 className="font-display text-3xl font-extrabold leading-[1.1] text-white sm:text-4xl lg:text-[48px]" data-id="element-347" data-mp="h5f0hpouu32s">
            {t.testimonials.title}
          </h2>
          <p className="mt-4 text-base text-white/65 sm:text-lg" data-id="element-348" data-mp="m2ydyndqabto">{t.testimonials.subtitle}</p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,4fr)] lg:items-center" data-id="element-349" data-mp="7a4yv32dbxpj">
          <div className="min-h-[280px] border-l-4 border-brand-500 pl-6 sm:min-h-[240px] sm:pl-8" data-id="element-350" data-mp="zwpp2073y3ri">
            <AnimatePresence mode="wait" data-id="element-351" data-mp="14v9b00lpbfjv">
              <motion.figure key={current.id} initial={{
              opacity: 0,
              x: 24
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-352" data-mp="14u7rd7qmdkqj">
                <div className="flex gap-1" aria-label="5 out of 5 stars" data-id="element-353" data-mp="1iuihg8k3uvan">
                  {Array.from({
                  length: 5
                }).map((_, i) => <StarIcon key={i} className="h-5 w-5 fill-brand-500 text-brand-500" aria-hidden="true" data-id="element-354" data-mp="1nnv5aggymy2c" data-mpi={i} />)}
                </div>
                <blockquote className="mt-5 font-display text-xl font-semibold italic leading-relaxed text-white sm:text-2xl" data-id="element-355" data-mp="1sjz7qzb6serx">
                  “{current.quote}”
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-4" data-id="element-356" data-mp="1jj12kdp0i17r">
                  <img src={portraits[current.id]} alt={current.name} className="h-14 w-14 rounded-full object-cover ring-2 ring-brand-500" loading="lazy" data-id="element-357" data-mp="1eiv34bci3af4" />
                  <span data-id="element-358" data-mp="14tv08guvnuvn">
                    <span className="block font-display text-base font-extrabold text-white" data-id="element-359" data-mp="14qz3uwyohe9b">
                      {current.name}
                    </span>
                    <span className="block text-sm text-white/55" data-id="element-360" data-mp="vrc22a21e6fq">{current.meta}</span>
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>

            <div className="mt-8 flex items-center gap-3" data-id="element-361" data-mp="32g49p7sgwknq">
              <button type="button" onClick={() => go(index - 1)} aria-label={t.testimonials.prev} className="inline-flex h-11 w-11 items-center justify-center rounded border border-white/25 text-white transition-colors duration-200 ease-smooth hover:border-brand-500 hover:text-brand-500" data-id="element-362" data-mp="37fqmjg4dvyw0">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-363" data-mp="2jobi9ht1r7i4" />
              </button>
              <button type="button" onClick={() => go(index + 1)} aria-label={t.testimonials.next} className="inline-flex h-11 w-11 items-center justify-center rounded border border-white/25 text-white transition-colors duration-200 ease-smooth hover:border-brand-500 hover:text-brand-500" data-id="element-364" data-mp="2onemx1547bpk">
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-365" data-mp="25077r145e2af" />
              </button>
              <div className="ml-2 flex gap-2" data-id="element-366" data-mp="1lxoltds2v2ch">
                {items.map((item, i) => <button key={item.id} type="button" onClick={() => setIndex(i)} aria-label={`${t.testimonials.goTo} ${i + 1}`} aria-current={i === index} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-smooth ${i === index ? 'w-7 bg-brand-500' : 'w-2 bg-white/30 hover:bg-white/60'}`} data-id="element-367" data-mp="1gyv57chhuv83" data-mpi={i} />)}
              </div>
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1" data-id="element-368" data-mp="29ly2y2ecj1xa">
            {items.map((item, i) => <li key={item.id} data-id="element-369" data-mp="k8krzubqafn7" data-mpi={i}>
                <button type="button" onClick={() => setIndex(i)} className={`flex w-full items-center gap-3 rounded border p-3 text-left transition-colors duration-200 ease-smooth ${i === index ? 'border-brand-500 bg-charcoal' : 'border-white/12 hover:border-white/35'}`} data-id="element-370" data-mp="k7t6jcbuhyan">
                  <img src={portraits[item.id]} alt="" aria-hidden="true" className="h-10 w-10 rounded-full object-cover" loading="lazy" data-id="element-371" data-mp="2m4tp63yrqeum" />
                  <span className="min-w-0" data-id="element-372" data-mp="2c8gtx1nkrsxr">
                    <span className="block truncate font-display text-sm font-bold text-white" data-id="element-373" data-mp="1y7e9etc8aqoy">
                      {item.name}
                    </span>
                    <span className="block truncate text-xs text-white/50" data-id="element-374" data-mp="236hg0i7ux4z1">{item.meta}</span>
                  </span>
                </button>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}